import React, { useEffect, useRef, useState } from "react";
import { LuLoaderCircle, LuX } from "react-icons/lu";
import * as SharedMessageTypes from "../../../darvin-shared/schema/zod/agent/message-types.schema";

interface ImagePreviewModalProps {
  img: SharedMessageTypes.UserMessageAttachment;
  isOpen: boolean;
  onClose: () => void;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  img,
  isOpen,
  onClose,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`${
          isClosing ? "animate-fade-out" : "animate-fade-in"
        } fixed inset-0 bg-black/75 flex justify-center items-center z-50 p-5 transition-opacity duration-300 ease-in-out`}
        onClick={handleClose}
        tabIndex={0}
        role="button"
      >
        <div className="relative w-full h-full flex flex-col items-center justify-center p-16">
          <button
            className="absolute top-0 right-0 w-9 h-9 rounded-full bg-black text-white text-2xl flex items-center justify-center hover:bg-white/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <LuX />
          </button>
          <div
            className={`${
              isClosing ? "scale-95 opacity-0" : "scale-100 opacity-100"
            }`}
          >
            <LazyImage attachment={img} />
          </div>
        </div>
      </div>
    </>
  );
};

function LazyImage({
  attachment,
}: {
  attachment: SharedMessageTypes.UserMessageAttachment;
}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  const getSrc = () => {
    if (attachment.content) {
      return `data:image/png;base64,${attachment.content}`;
    } else if (attachment.url) {
      if (import.meta.env.DEV) {
        // Below attachment url is constructed for local development to allow Vite proxy to work
        return attachment.url.replace("https://dev.darvin.dev", "");
      }
      return attachment.url;
    }
    return undefined;
  };

  useEffect(() => {
    console.log("LazyImage useEffect", attachment);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="flex items-center justify-center">
      {!isInView ? (
        <div className="w-20 h-20 flex items-center justify-center">
          <LuLoaderCircle className="animate-spin" size={24} />
        </div>
      ) : (
        <img
          src={getSrc()}
          alt="Attached image"
          loading="lazy"
          style={{
            opacity: isInView ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
          className="max-w-full max-h-[95vh] object-contain rounded transition-all duration-300 ease-in-out"
        />
      )}
    </div>
  );
}
